import { toNestErrors } from '@hookform/resolvers';

import deepEqual from 'fast-deep-equal';
import memoizeOne from 'memoize-one';
import {
  appendErrors,
  type FieldError,
  type FieldValues,
  type Resolver,
} from 'react-hook-form';
import type * as Yup from 'yup';
import { assertNoCustomInstances } from './assertNoCustomInstances.ts';

/**
 * Why `path!` ? because it could be `undefined` in some case
 * https://github.com/jquense/yup#validationerrorerrors-string--arraystring-value-any-path-string
 */
function parseErrorSchema(
  error: Yup.ValidationError,
  validateAllFieldCriteria: boolean,
) {
  return (error.inner || []).reduce<Record<string, FieldError>>(
    (previous, error) => {
      if (!previous[error.path!]) {
        previous[error.path!] = { message: error.message, type: error.type! };
      }

      if (validateAllFieldCriteria) {
        const types = previous[error.path!].types;
        const messages = types?.[error.type!];

        previous[error.path!] = appendErrors(
          error.path!,
          validateAllFieldCriteria,
          previous,
          error.type!,
          messages
            ? ([] as string[]).concat(messages as string[], error.message)
            : error.message,
        ) as FieldError;
      }

      return previous;
    },
    {},
  );
}

/**
 * Soglia in ms per considerare due chiamate consecutive come "digitazione rapida".
 * Se il resolver viene chiamato entro questa soglia dalla chiamata precedente,
 * la validazione viene debounced.
 */
const RAPID_FIRE_MS = 250;

/**
 * Tempo di debounce in ms. Dopo questo periodo di inattività, la validazione
 * reale viene eseguita.
 */
const DEBOUNCE_MS = 120;

/**
 * Cede il controllo al browser per permettere il paint del keystroke
 * PRIMA che la validazione (~105ms) blocchi il main thread.
 * `setTimeout(0)` schedula una macro task: il browser può paintare nel frattempo.
 */
const yieldToMain = () => new Promise<void>(resolve => setTimeout(resolve, 0));

/**
 * Versione memoizzata, debounced e yield-aware del resolver Yup per RHF.
 *
 * **Yield**: per le validazioni field-level, cede il controllo al browser
 * ({@link yieldToMain}) prima di eseguire `validateSync`, così il paint del
 * keystroke non viene bloccato. In fase di submit lo yield è omesso.
 *
 * **Debounce**: durante la digitazione rapida (keystroke entro
 * {@link RAPID_FIRE_MS} dalla precedente), il resolver ritorna il risultato
 * cached e schedula la validazione reale dopo {@link DEBOUNCE_MS} di
 * inattività. La prima chiamata (dopo una pausa) e il submit eseguono
 * sempre la validazione immediatamente.
 */
export function yupMemoizedResolver<TFieldValues extends FieldValues>(
  liveSchema: Yup.ObjectSchema<Yup.AnyObject>,
  schemaOptions: Parameters<Yup.ObjectSchema<TFieldValues>['validate']>[1] = {},
  resolverOptions: {
    /**
     * Return the raw input values rather than the parsed values.
     * @default false
     */
    raw?: boolean;
  } = {},
): Resolver<TFieldValues> {
  /**
   * Memoizziamo la funzione di validazione: `deepEqual` (simmetrico)
   * confronta i valori clonati con lo snapshot precedente.
   *
   * Il `structuredClone` è fuori dalla funzione memoizzata perché RHF passa
   * sempre la stessa referenza oggetto (_formValues) al resolver.
   * Senza clone, memoizeOne confronterebbe la stessa referenza mutata
   * -> cache hit perpetua -> errori mai aggiornati.
   *
   * NOTA: NON usare `clonePlainData` qui. I valori del form possono contenere
   * host objects (File, RcFile, Blob) che `clonePlainData` degraderebbe a
   * plain objects `{}`, perdendo prototype e dati binari. `structuredClone`
   * gestisce correttamente questi tipi tramite lo structured clone algorithm.
   */
  const memoizedValidate = memoizeOne(
    (values: any, context: any) => {
      // Yup ValidationError è poco ottimizzato.
      //
      // ATTENZIONE: durante validateSync, qualsiasi errore non-Yup (es. un bug
      // in un transform custom) avrà stack trace assente. Il `finally` ripristina
      // immediatamente i valori originali, limitando la finestra al solo validateSync.
      // NOTE: Valutare se in sviluppo è preferibile disabilitare la patch per
      // avere stack trace completi anche per errori non-Yup.
      const realCaptureStackTrace = Error.captureStackTrace;
      const realStackTraceLimit = Error.stackTraceLimit;
      Error.captureStackTrace = undefined as any;
      Error.stackTraceLimit = 0;

      try {
        const result = liveSchema.validateSync(values, {
          abortEarly: false,
          ...schemaOptions,
          context,
        });
        return { type: 'success', result } as const;
      } catch (e: any) {
        if (!e.inner) {
          throw e;
        }
        return { type: 'error', error: e } as const;
      } finally {
        Error.captureStackTrace = realCaptureStackTrace;
        Error.stackTraceLimit = realStackTraceLimit;
      }
    },
    (newArgs, lastArgs) => {
      const valuesEqual = deepEqual(newArgs[0], lastArgs[0]);
      const contextEqual = deepEqual(newArgs[1], lastArgs[1]);
      return valuesEqual && contextEqual;
    },
  );

  /**
   * Esegue la validazione completa: clone -> memoizedValidate -> format result.
   *
   * `values` è la referenza diretta a `_formValues` di RHF.
   * `structuredClone` cattura uno snapshot immutabile per la memoizzazione.
   *
   * I valori del form devono contenere solo tipi serializzabili
   * (primitive, plain objects, arrays, File, Blob).
   * Classi custom (es. Decimal) causano DataCloneError.
   */
  function runValidation(values: any, context: any, options: any) {
    if (process.env.NODE_ENV === 'development') {
      assertNoCustomInstances(values);
    }
    const clonedValues = structuredClone(values);
    const result = memoizedValidate(clonedValues, context);

    if (result.type === 'error') {
      return {
        values: {},
        errors: toNestErrors(
          parseErrorSchema(
            result.error,
            !options.shouldUseNativeValidation &&
              options.criteriaMode === 'all',
          ),
          options,
        ),
      };
    }

    return {
      values: resolverOptions.raw ? values : result.result,
      errors: {},
    };
  }

  // --- Stato del debounce (closure del resolver) ---
  let cachedResult: any = null;
  let lastCallTs = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingResolves: Array<{
    resolve: (r: any) => void;
    reject: (e: unknown) => void;
  }> = [];

  // --- Il resolver vero e proprio ---
  return async (values, context, options) => {
    if (schemaOptions.context && process.env.NODE_ENV === 'development') {
      console.warn(
        "You should not used the yup options context. Please, use the 'useForm' context object instead",
      );
    }

    // Yield al browser per field-level validation: permette il paint del
    // campo di input (es. checkbox, carattere nel text input) PRIMA che
    // validateSync/cast blocchino il main thread.
    //
    // In fase di submit (!options.names) NON effettuiamo lo yield:
    // l'utente si aspetta una risposta immediata.
    const isFieldLevel = !!options.names;
    if (isFieldLevel) {
      await yieldToMain();
    }

    const now = Date.now();
    const timeSinceLastCall = now - lastCallTs;
    lastCallTs = now;
    const isRapidFire =
      isFieldLevel && timeSinceLastCall < RAPID_FIRE_MS && cachedResult;

    // Cancella eventuale debounce precedente
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }

    // Caso A) Submit, prima chiamata, o dopo pausa: validazione immediata
    if (!isRapidFire) {
      try {
        cachedResult = runValidation(values, context, options);
        for (const p of pendingResolves) p.resolve(cachedResult);
      } catch (err) {
        for (const p of pendingResolves) p.reject(err);
        throw err;
      } finally {
        pendingResolves = [];
      }
      return cachedResult;
    }

    // Caso B) Digitazione rapida -> debounce: ritorna promise che si risolve
    // con la validazione reale dopo il periodo di debounce
    return new Promise((resolve, reject) => {
      pendingResolves.push({ resolve, reject });

      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        try {
          // `values` è la ref mutabile di RHF: structuredClone dentro
          // runValidation cattura lo stato aggiornato all'ultimo keystroke
          cachedResult = runValidation(values, context, options);
          for (const p of pendingResolves) p.resolve(cachedResult);
        } catch (err) {
          for (const p of pendingResolves) p.reject(err);
        } finally {
          pendingResolves = [];
        }
      }, DEBOUNCE_MS);
    });
  };
}
